// Quote Builder — the supplier string that goes ON A QUOTE LINE.
//
// ── Why this is its own module ────────────────────────────────────────────
// Two surfaces commit catalogue rows to a quote and both have to spell the
// supplier the same way: the left panel's search (products-panel.ts) and the
// right column's add-item box (add-item-search.ts). They resolved it
// differently, and the difference reached live data — 267 quote lines saying
// "TKC" and one saying "TKC Components", written by the add-item box.
//
// Purchase orders match suppliers on that string, so two spellings is two
// suppliers: half the order routes and half does not. This is the same class
// of hazard as suppliers.name being non-editable (project note 2026-08-18) —
// the name IS the key.
//
// ── The rule ──────────────────────────────────────────────────────────────
// A catalogue row from /catalogue/search carries `supplierSlug` (stable) and
// `supplierName` (the suppliers table's own spelling). The NAME is not what
// the quote uses:
//
//   suppliers.name          "TKC Components"
//   what every tab says     "TKC"          <- SUPPLIERS[].supplierKey
//   what 267 live lines say "TKC"
//
// So the slug is resolved against the platform's own registry first, and the
// catalogue's name is only a fallback for a supplier the registry has never
// heard of (any newly imported catalogue). That fallback is exact, not a
// guess: the QB builds a tab for such a supplier from the very same
// `suppliers.name` value, so the tab and the line agree by construction.

import { SUPPLIERS } from "./types";

/**
 * The canonical quote-line supplier string for a catalogue slug.
 *
 * @param slug          `supplierSlug` from a catalogue row ("tkc", "pws").
 * @param catalogueName `supplierName` from the same row — used only when the
 *                      registry does not know the slug.
 *
 * Returns "" for an empty slug with no fallback, so a caller can tell "no
 * answer" from a wrong one rather than being handed an invented label.
 */
export function supplierLabelForSlug(slug: string, catalogueName?: string): string {
  const s = String(slug || "").trim().toLowerCase();
  if (!s) return catalogueName ?? "";
  const def = SUPPLIERS.find((d) => d.catalogueSlug === s);
  return def?.supplierKey ?? def?.label ?? catalogueName ?? s.toUpperCase();
}
